import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Segmented,
  Space,
  Spin,
  Statistic,
  Tooltip,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import {
  BucketStatus,
  HealthHistoryBucket,
} from "../../../interfaces/paymentHealth.interface";
import {
  fetchPaymentHealthHistoryThunk,
  selectPaymentHealthHistory,
  selectPaymentHealthHistoryLoading,
} from "../paymentHealthSlice";

const SLOT_META: Record<BucketStatus, { color: string; label: string }> = {
  operational: { color: "#52c41a", label: "Ran & passed" },
  incident: { color: "#faad14", label: "Incident" },
  unknown: { color: "#8c8c8c", label: "Monitor blind" },
  missing: { color: "#e8e8e8", label: "No run recorded" },
};

const HISTORY_POLL_INTERVAL_MS = 60_000;
const TICK_COUNT = 5;

interface RangeOption {
  label: string;
  value: number; // days
}
const RANGE_OPTIONS: RangeOption[] = [
  { label: "24h", value: 1 },
  { label: "7d", value: 7 },
];

const HealthTimeline: React.FC = () => {
  const dispatch = useAppDispatch();
  const history = useAppSelector(selectPaymentHealthHistory);
  const loading = useAppSelector(selectPaymentHealthHistoryLoading);
  const [days, setDays] = useState<number>(1);

  // Refetch from the server whenever the range changes; the server re-aggregates
  // for that window (we never fetch-then-slice on the client).
  useEffect(() => {
    dispatch(fetchPaymentHealthHistoryThunk(days));
    const id = setInterval(() => {
      dispatch(fetchPaymentHealthHistoryThunk(days));
    }, HISTORY_POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [dispatch, days]);

  const buckets = history?.buckets ?? [];
  const summary = history?.summary;
  const hasData = (summary?.total ?? 0) > 0;

  const ticks = useMemo(() => {
    if (!history) return [] as string[];
    const startMs = new Date(history.windowStart).getTime();
    const endMs = new Date(history.windowEnd).getTime();
    const spanMs = endMs - startMs;
    const fmt = spanMs <= 36 * 60 * 60 * 1000 ? "HH:mm" : "MMM D";
    return Array.from({ length: TICK_COUNT }, (_, i) => {
      const t = startMs + (spanMs * i) / (TICK_COUNT - 1);
      return dayjs(t).format(fmt);
    });
  }, [history]);

  return (
    <Card size="small" style={{ marginBottom: 24 }}>
      <Space
        style={{ width: "100%", justifyContent: "space-between" }}
        align="start"
        wrap
      >
        <div>
          <Typography.Text strong>Health check timeline</Typography.Text>
          <br />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {history
              ? `${buckets.length} buckets · one bar ≈ ${_humanizeMinutes(
                  history.bucketMinutes
                )}`
              : "Loading history…"}
          </Typography.Text>
        </div>
        <Space size="large" wrap>
          <Segmented
            size="small"
            value={days}
            onChange={(v) => setDays(v as number)}
            options={RANGE_OPTIONS}
          />
          {hasData && summary && (
            <>
              <Statistic
                title="Uptime"
                value={summary.uptimePct}
                suffix="%"
                valueStyle={{
                  color: summary.uptimePct >= 99 ? "#52c41a" : "#faad14",
                  fontSize: 20,
                }}
              />
              <Statistic
                title="Checks passed"
                value={summary.operational}
                suffix={`/ ${summary.total}`}
                valueStyle={{ fontSize: 20 }}
              />
            </>
          )}
        </Space>
      </Space>

      {loading && !history ? (
        <div style={{ textAlign: "center", padding: 24 }}>
          <Spin />
        </div>
      ) : !hasData ? (
        <Typography.Text type="secondary">
          No health-check history yet — the timeline fills in as the monitor
          runs.
        </Typography.Text>
      ) : (
        <Spin spinning={loading} size="small">
          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              gap: 2,
              height: 40,
              marginTop: 12,
            }}
          >
            {buckets.map((b) => {
              const meta = SLOT_META[b.status];
              return (
                <Tooltip
                  key={b.start}
                  title={<BucketTooltip bucket={b} />}
                  mouseEnterDelay={0.05}
                >
                  <div
                    style={{
                      flex: "1 1 0",
                      minWidth: 2,
                      height: 36,
                      borderRadius: 2,
                      background: meta.color,
                    }}
                  />
                </Tooltip>
              );
            })}
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: 4,
            }}
          >
            {ticks.map((t, i) => (
              <Typography.Text
                key={`${t}-${i}`}
                type="secondary"
                style={{ fontSize: 11 }}
              >
                {t}
              </Typography.Text>
            ))}
          </div>
        </Spin>
      )}

      <Space size="middle" style={{ marginTop: 12 }} wrap>
        {(Object.keys(SLOT_META) as BucketStatus[]).map((key) => (
          <Space size={4} key={key}>
            <span
              style={{
                display: "inline-block",
                width: 10,
                height: 10,
                borderRadius: 2,
                background: SLOT_META[key].color,
              }}
            />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {SLOT_META[key].label}
            </Typography.Text>
          </Space>
        ))}
      </Space>
    </Card>
  );
};

const BucketTooltip: React.FC<{ bucket: HealthHistoryBucket }> = ({
  bucket,
}) => {
  const meta = SLOT_META[bucket.status];
  const sameDay = dayjs(bucket.start).isSame(bucket.end, "day");
  const range = `${dayjs(bucket.start).format("MMM D, HH:mm")} – ${dayjs(
    bucket.end
  ).format(sameDay ? "HH:mm" : "MMM D, HH:mm")}`;
  const missed = Math.max(0, bucket.expected - bucket.observed);
  const bucketUptime = bucket.observed
    ? Math.round((bucket.operational / bucket.observed) * 100)
    : 0;

  return (
    <div style={{ fontSize: 12, lineHeight: 1.6 }}>
      <div style={{ fontWeight: 600 }}>{meta.label}</div>
      <div>{range}</div>
      {bucket.observed > 0 ? (
        <>
          <div>
            {bucket.operational}/{bucket.observed} checks passed ({bucketUptime}
            %)
          </div>
          {bucket.incident > 0 && <div>Incident runs: {bucket.incident}</div>}
          {bucket.unknown > 0 && <div>Monitor blind: {bucket.unknown}</div>}
          {missed > 0 && <div>Missed runs: {missed}</div>}
        </>
      ) : (
        <div>No runs recorded in this window</div>
      )}
    </div>
  );
};

function _humanizeMinutes(minutes: number): string {
  if (!minutes || minutes <= 0) return "—";
  if (minutes < 60) return `${minutes} min`;
  const hours = minutes / 60;
  if (hours < 24) {
    return Number.isInteger(hours)
      ? `${hours} h`
      : `${hours.toFixed(1).replace(/\.0$/, "")} h`;
  }
  return `${(hours / 24).toFixed(1).replace(/\.0$/, "")} d`;
}

export default HealthTimeline;
